import React, { useMemo, useState } from "react";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { displayAccountLabel } from "@common/helpers/accounting";
import { AccountRole } from "@common/types/domains/accounting/account";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useClasses } from "@api/class.api";
import { AccountCombobox } from "@components/accounts/AccountCombobox";
import { ClassAvatarGroup } from "@components/avatar/ClassAvatarGroup";
import { ClassWithChildren } from "@helpers/class";
import { useAccounts } from "@hooks/useAccounts";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { ClassCombobox } from "@components/transactions/TransactionSplitV2/ClassCombobox";
import { getClassesAriaLabel } from "@components/transactions/TransactionSplitV2/getClassesAriaLabel";
import styles from "./InvoiceFieldSettingsPanel.module.scss";

interface AccountCellProps {
    entityId: number;
    accountId: string | null;
    allowedAccountTypes?: AccountType[];
    defaultAccountRole?: AccountRole;
    onChange: (accountId: string | null) => void;
}

export const ItemAdjustmentAccountCell: React.FC<AccountCellProps> = ({
    entityId,
    accountId,
    allowedAccountTypes,
    defaultAccountRole,
    onChange,
}) => {
    const showAccountNumbers = useShowAccountNumbers();
    const { accounts } = useAccounts({ entityId });
    const selectedAccount = accounts.find(
        (account) => account.id === accountId,
    );
    const defaultAccount =
        defaultAccountRole == null
            ? undefined
            : accounts.find((account) => account.role === defaultAccountRole);
    const placeholderTrigger =
        accountId == null && defaultAccount != null ? (
            <Picker.CellTrigger
                placeholder={displayAccountLabel(
                    defaultAccount,
                    showAccountNumbers,
                )}
            />
        ) : undefined;

    return (
        <AccountCombobox
            entityId={entityId}
            selectedAccount={selectedAccount}
            allowedAccountTypes={allowedAccountTypes}
            showAccountCode={showAccountNumbers}
            trigger={placeholderTrigger}
            canClear
            onChange={(account) => onChange(account?.id ?? null)}
        />
    );
};

interface ClassCellProps {
    classIds: string[];
    onChange: (classIds: string[]) => void;
}

export const ItemAdjustmentClassCell: React.FC<ClassCellProps> = ({
    classIds,
    onChange,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const { classesMap, sortedClassesIndexMap } = useClasses();

    const selectedClasses = useMemo(() => {
        if (classIds.length === 0) {
            return [];
        }
        return classIds
            .map((id) => classesMap.get(id))
            .filter((classItem): classItem is ClassWithChildren =>
                Boolean(classItem),
            )
            .toSorted((left, right) => {
                const leftIndex =
                    sortedClassesIndexMap.get(left.id) ?? Infinity;
                const rightIndex =
                    sortedClassesIndexMap.get(right.id) ?? Infinity;
                return leftIndex - rightIndex;
            });
    }, [classIds, classesMap, sortedClassesIndexMap]);

    const trigger = useMemo(
        () => (
            <Picker.CellContent
                className={styles.classSelect}
                value={
                    selectedClasses.length === 0 ? undefined : (
                        <ClassAvatarGroup classes={selectedClasses} max={3} />
                    )
                }
                aria-label={getClassesAriaLabel(selectedClasses)}
            />
        ),
        [selectedClasses],
    );

    return (
        <ClassCombobox
            selectedClassIds={classIds}
            onChange={onChange}
            hideArrow
            open={isOpen}
            onOpenChange={setIsOpen}
            trigger={trigger}
        />
    );
};
